import { Route } from "react-router-dom";
import { apiDocsPlugin, ApiExplorerPage } from "@backstage/plugin-api-docs";
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from "@backstage/plugin-catalog";
import {
  CatalogImportPage,
  catalogImportPlugin,
} from "@backstage/plugin-catalog-import";
import { ScaffolderPage, scaffolderPlugin } from "@backstage/plugin-scaffolder";
import { orgPlugin } from "@backstage/plugin-org";
import { SearchPage } from "@backstage/plugin-search";
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from "@backstage/plugin-techdocs";
import { TechDocsAddons } from "@backstage/plugin-techdocs-react";
import { ReportIssue } from "@backstage/plugin-techdocs-module-addons-contrib";
import { UserSettingsPage } from "@backstage/plugin-user-settings";
import { apis } from "./apis";
import { entityPage } from "./components/catalog/EntityPage";
import { searchPage } from "./components/search/SearchPage";
import { Root } from "./components/Root";
import { UnifiedThemeProvider } from "@backstage/theme";
import { baseTheme } from "./theme/devleaps-theme";
import CssBaseline from "@material-ui/core/CssBaseline";

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from "@backstage/core-components";
import { createApp } from "@backstage/app-defaults";
import { AppRouter, FlatRoutes } from "@backstage/core-app-api";
import { CatalogGraphPage } from "@backstage/plugin-catalog-graph";
import { RequirePermission } from "@backstage/plugin-permission-react";
import { catalogEntityCreatePermission } from "@backstage/plugin-catalog-common/alpha";
import { ForgePage } from "@internal/plugin-forge";
import { microsoftAuthApiRef } from "@backstage/core-plugin-api";
import { OdooTimesheetPage } from "@internal/plugin-odoo-timesheet";

import { CirclesPage, circlesAccessPermission } from "@internal/plugin-circles";
import { ResumeGeneratorPage } from "@internal/plugin-resume-generator";
import { resumeGeneratorAccessPermission } from "./permissions";
import {
  CompensationForgePage,
  compensationForgeAccessPermission,
} from "@internal/plugin-compensation-forge";
import { StanPage } from "@internal/plugin-stan";
import { CrmlyPage, crmonReadPermission } from "@internal/plugin-crmon";
import { AgentPoliciesPage } from "@internal/plugin-agent-policies";

import { HomePage } from "./components/home";

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: (props) => {
      if (process.env.NODE_ENV === "production") {
        return (
          <SignInPage
            {...props}
            providers={[
              {
                id: "microsoft-auth-provider",
                title: "Microsoft",
                message: "Sign in using Microsoft",
                apiRef: microsoftAuthApiRef,
              },
            ]}
          />
        );
      }
      return <SignInPage {...props} providers={["guest"]} />;
    },
  },
  themes: [
    {
      id: "devleaps-theme",
      title: "Devleaps Theme",
      variant: "light",
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={baseTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/forge/*" element={<ForgePage />} />
    <Route path="/agent-policies/*" element={<AgentPoliciesPage />} />
    <Route path="/odoo-timesheet/*" element={<OdooTimesheetPage />} />
    <Route path="/stan/*" element={<StanPage />} />
    <Route
      path="/crmon/*"
      element={
        <RequirePermission permission={crmonReadPermission}>
          <CrmlyPage />
        </RequirePermission>
      }
    />
    <Route
      path="/circles"
      element={
        <RequirePermission permission={circlesAccessPermission}>
          <CirclesPage />
        </RequirePermission>
      }
    />
    <Route
      path="/resume-generator"
      element={
        <RequirePermission permission={resumeGeneratorAccessPermission}>
          <ResumeGeneratorPage />
        </RequirePermission>
      }
    />
    <Route
      path="/compensation-forge"
      element={
        <RequirePermission permission={compensationForgeAccessPermission}>
          <CompensationForgePage />
        </RequirePermission>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>
);
